import React, {
   memo,
   useCallback,
   useEffect,
   useMemo,
   useRef,
   useState,
} from 'react'
import { Portal } from 'react-portal'
import * as styles from '../../tasks/tasksList/tasksList.module.scss'
import { SnackBarState } from '../../snackBar/types'
import { useAuditsTotals, useNotifications, useNotificationTotals } from '../../../../hooks'
import { Severity } from '../../../../types'
import SnackBar, { closedSnackbarState } from '../../snackBar/snackBar'
import { Button, LoadingOverlay, NoResults } from '../..'
import { Item } from '../../../../hooks/useUserItems/useUserItems.type'
import { NoteModal } from '../../../views/notes/noteModal'

import { ItemDetails } from '../../tasks/tasksList/task/types'
import { Task } from '../../tasks'
import { getTaskTypeFromType } from '../../../views/tasks/helpers'
import { TasksProps } from './types'

const Tasks = ({
   isEmployeeUser,
   itemId,
   onActionTaskComplete,
   parentTaskListDetails,
}: TasksProps) => {
   const listRef = useRef<HTMLUListElement>(null)
   const [currentPage, setCurrentPage] = useState<number>(1)
   const [snackbar, setSnackbar] = useState<SnackBarState>(closedSnackbarState)

   const { refresh: refreshTotal, totalNotifications: totalTasks } =
      useNotificationTotals({
         toolId: itemId,
      })

   const {
      acceptReject,
      dismiss,
      isLastPage,
      isLoading: isTasksLoading,
      isLoadingMore: isPaging,
      list,
      isDismissSuccess,
      isAcceptRejectSuccess,
      refetch: refreshCurrentPage,
   } = useNotifications({
      onChange: refreshTotal,
      totalItems: totalTasks,
      notificationType: null,
      currentPage: currentPage,
      toolId: itemId,
   })

   const { refetch: refreshParentTaskList } = useNotifications({
      // Initialise with values from the component if not a child
      notificationType: null,
      currentPage,
      totalItems: totalTasks,
      ...parentTaskListDetails
   });
   const { refetch: refreshAuditTotals } = useAuditsTotals();
   const { refresh: refreshNotificationTotals } = useNotificationTotals({});

   const refreshTotalsAndTasks = useCallback(() => {
      // If component is a child of a taskList, refresh totals and lists
      if (parentTaskListDetails) {
         refreshAuditTotals()
         refreshNotificationTotals()
         refreshParentTaskList()
      }
   },
   [])

   useEffect(() => {
      if (isDismissSuccess) {
         refreshTotalsAndTasks()
         setSnackbar({
            message: 'The task was dismissed',
            severity: Severity.SUCCESS,
         })
      }
   }, [isDismissSuccess, refreshTotalsAndTasks])

   useEffect(() => {
      if (isAcceptRejectSuccess) {
         refreshTotalsAndTasks()
         setSnackbar({
            message: 'The action was updated',
            severity: Severity.SUCCESS,
         })
         setTimeout(() => onActionTaskComplete(), 500)
      }
   }, [isAcceptRejectSuccess, onActionTaskComplete, refreshTotalsAndTasks])

   // Start of Note Modal Functionality
   const [isNoteModalVisible, setIsNoteModalVisible] = useState<boolean>(false)
   const [selectedNoteId, setSelectedNoteId] = useState<number | null>(null)
   const [selectedItem, setSelectedItem] = useState<Item | null>(null)

   const handleViewNote = useCallback((itemData: ItemDetails) => {
      const { NoteId, ToolLoanId, ToolId, Barcode, CustomText1Value, Title } =
         itemData
      const stringToolId = String(ToolId)
      if (!NoteId) {
         return setSnackbar({
            message: 'There is no note associated with this task',
            severity: Severity.ERROR,
         })
      }
      setSelectedItem({
         ID: stringToolId,
         Title: Title,
         Barcode: Barcode,
         SerialNumber: '',
         CustomText1Value: CustomText1Value,
         ToolLoanId: ToolLoanId,
      })
      setSelectedNoteId(NoteId)
      setIsNoteModalVisible(true)
   }, [])

   const handleNoteModalSave = useCallback(() => {
      // clear selected ids on note modal close
      setSelectedNoteId(null)
      setIsNoteModalVisible(false)
      setSnackbar({
         message: 'The Note was updated',
         severity: Severity.SUCCESS,
      })
      refreshCurrentPage()
   }, [refreshCurrentPage])
   // End of Note Modal Functionality

   const handleDismissComplete = useCallback(
      (id: number) => {
         dismiss(id)
         refreshTotal()
      },
      [dismiss, refreshTotal]
   )

   const handleAcceptDecline = useCallback(
      (id: number, accept: boolean) => {
         acceptReject(id, accept)
         refreshTotal()
      },
      [acceptReject, refreshTotal]
   )

   const handlePreviousPage = useCallback(() => {
      setCurrentPage(currentPage - 1)
   }, [currentPage])

   const handleNextPage = useCallback(() => {
      setCurrentPage(currentPage + 1)
   }, [currentPage])

   const showList = useMemo(
      () => list?.length > 0 && !isTasksLoading,
      [list, isTasksLoading]
   )

   const showPaging = useMemo(
      () => totalTasks > 10 && !isTasksLoading,
      [totalTasks, isTasksLoading]
   )

   const showSpinner = useMemo(
      () => isTasksLoading || isPaging,
      [isTasksLoading, isPaging]
   )

   return (
      <section className={styles.itemTasksContainer}>
         <div className={styles.content}>
            {showSpinner && (
               <LoadingOverlay
                  positionAbsolute={isTasksLoading || isPaging}
                  size="small"
               />
            )}

            {list?.length === 0 && !showSpinner && (
               <NoResults>
                  <p>All Caught Up!</p>
               </NoResults>
            )}

            {showList && (
               <ul className={styles.list} ref={listRef}>
                  {list.map((task) => (
                     <li key={task.Id}>
                        <Task
                           taskType={getTaskTypeFromType(task.Type)}
                           taskDetails={task}
                           onAcceptDeclineClick={handleAcceptDecline}
                           onDismissClick={handleDismissComplete}
                           onCompleteClick={handleDismissComplete}
                           onViewItemClick={null}
                           onViewNoteClick={handleViewNote}
                           isItemsModalTask
                        />
                     </li>
                  ))}
               </ul>
            )}

            {showPaging && (
               <div className={styles.pagingButtons}>
                  <Button
                     onClick={handlePreviousPage}
                     disabled={currentPage === 1}
                     variant="text"
                     minWidth="max-content"
                     small
                  >
                     Previous Page
                  </Button>
                  <Button
                     onClick={handleNextPage}
                     disabled={isLastPage}
                     variant="text"
                     minWidth="max-content"
                     small
                  >
                     Next Page
                  </Button>
               </div>
            )}
         </div>
         {isNoteModalVisible && (
            <Portal>
               <NoteModal
                  noteId={selectedNoteId}
                  itemDetails={selectedItem}
                  isModalOpen={isNoteModalVisible}
                  setIsModalOpen={setIsNoteModalVisible}
                  onSaveNote={handleNoteModalSave}
                  isReadOnly={isEmployeeUser}
               />
            </Portal>
         )}
         <SnackBar
            open={
               snackbar.message !== '' && snackbar.severity !== Severity.INFO
            }
            message={snackbar.message}
            severity={snackbar.severity}
            resetMessageStateHandler={() => setSnackbar(closedSnackbarState)}
         />
      </section>
   )
}

export default memo(Tasks)
